import { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import Login from 'services/api/Login';
import Http from 'services/Http';
import { handleLogin } from 'utils/auth';
import { signalAcceptedPasskeys } from './signalAcceptedPasskeys';
import {
  getWebAuthnError,
  isWebAuthnSupported,
  preparePublicKeyCredential,
  preparePublicKeyOptions,
} from './webAuthn';

/**
 * Custom hook for handling passkey authentication in web
 */
export default function usePasskeyAuth() {
  const navigate = useNavigate();
  const [isCheckingPasskey, setIsCheckingPasskey] = useState(false);
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const locale = useSelector(state => state.i18n.locale);

  // Ref to prevent multiple simultaneous passkey attempts
  const isAuthenticatingRef = useRef(false);

  /**
   * Check if a passkey is available for the phone number
   * @param {string} phoneNumber - The formatted phone number
   * @param {string} prefix - Country code prefix
   * @returns {Promise<boolean | { hasPasskey: boolean, loginType: string | null }>}
   */
  const checkPasskeyAvailability = useCallback(async (phoneNumber, prefix) => {
    if (!phoneNumber || !isWebAuthnSupported()) {
      return false;
    }

    try {
      setIsCheckingPasskey(true);

      // Query the server for authentication options
      const response = await Http.post('/v1/webauthn/authenticate/begin', {
        phone: prefix + phoneNumber,
        passkeyUpgradeSupported: true,
      });

      const requestJson = response.data;
      const loginType = requestJson?.loginType || null;

      // If we got credential options with allowCredentials array, a passkey is available
      const hasPasskey = !!(
        requestJson?.options?.allowCredentials && requestJson.options.allowCredentials.length > 0
      );

      setPasskeyAvailable(hasPasskey);
      return { hasPasskey, loginType };
    } catch {
      setPasskeyAvailable(false);
      return false;
    } finally {
      setIsCheckingPasskey(false);
    }
  }, []);

  /**
   * Authenticate with passkey
   * @param {string} phoneNumber - The formatted phone number
   * @param {string} prefix - Country code prefix
   * @param {Object} additionalData - Extra data needed
   * @returns {Object} Result with success and passkey flags
   */
  const authenticateWithPasskey = useCallback(
    async (phoneNumber, prefix, additionalData = {}) => {
      // If passkey not supported by browser, skip
      if (!phoneNumber || !isWebAuthnSupported()) {
        return { success: false, passkey: false };
      }

      // Prevent multiple simultaneous passkey attempts
      if (isAuthenticatingRef.current) {
        return { success: false, passkey: false, skipped: true };
      }

      isAuthenticatingRef.current = true;

      try {
        setIsCheckingPasskey(true);

        // Always fetch a fresh challenge so sign-in never reuses the availability-check challenge.
        const response = await Http.post('/v1/webauthn/authenticate/begin', {
          phone: prefix + phoneNumber,
          passkeyUpgradeSupported: true,
        });
        const requestJson = response.data;

        if (!requestJson) {
          setPasskeyAvailable(false);
          throw new Error('Failed to get authentication options from server');
        }

        const credentialOptions = requestJson.options || requestJson;
        const { userId } = requestJson;

        if (!credentialOptions || !credentialOptions.challenge) {
          setPasskeyAvailable(false);
          throw new Error('Invalid authentication options from server');
        }

        // If we got valid options, a passkey is available
        setPasskeyAvailable(true);

        try {
          // Prepare the options for WebAuthn API
          const publicKeyOptions = preparePublicKeyOptions(credentialOptions);

          // Request the credential from the browser/passkey provider (e.g., LastPass)
          const credential = await navigator.credentials.get({
            publicKey: publicKeyOptions,
          });

          // Prepare the credential for the server
          const processedCredential = preparePublicKeyCredential(credential);
          const authData = {
            ...processedCredential,
            userId,
            keepSessionOpen: additionalData.keepSessionOpen,
            inviteToken: additionalData.inviteToken,
            passkeyUpgradeSupported: true,
          };

          // Complete authentication on the server
          const authResult = await Http.post('/v1/webauthn/authenticate/complete', authData);
          const token = authResult?.data?.token;
          const user = authResult?.data?.user;

          if (authResult?.data?.upgradeRequired && authResult?.data?.pendingId && !token?.hash) {
            return {
              success: false,
              upgradeRequired: true,
              pendingId: authResult.data.pendingId,
              phoneNumber,
              prefix,
            };
          }

          if (token?.hash) {
            // After sign-in only: prune revoked passkeys for the next attempt.
            await signalAcceptedPasskeys({
              rpId: credentialOptions.rpId,
              userId,
              credentialIds: credentialOptions.allowCredentials?.map(({ id }) => id),
            });

            // Use the auth utility to handle login
            handleLogin(token, user, additionalData.keepSessionOpen, additionalData.returnUrl);
            return { success: true, passkey: true };
          }
          return { success: false, passkey: true };
        } catch (passkeyError) {
          // This is likely a user cancellation of the passkey dialog
          // Get detailed error information
          const error = getWebAuthnError(passkeyError);

          // Only navigate if not already on the cancelled screen
          if (!additionalData.skipNavigateOnCancel) {
            navigate('/auth/login/passkey-cancelled', {
              state: {
                ...additionalData,
                error,
                phoneNumber,
                countryCode: prefix,
                locale,
              },
              replace: true,
            });
          }

          return {
            success: false,
            canceled: true,
            passkey: true,
            error,
          };
        }
      } catch (error) {
        // This is an error before showing the passkey dialog
        setPasskeyAvailable(false);
        return { success: false, error, passkey: false };
      } finally {
        setIsCheckingPasskey(false);
        isAuthenticatingRef.current = false;
      }
    },
    [navigate, locale],
  );

  /**
   * Handle SMS fallback when passkey is not available or user cancels
   * @param {string} phoneNumber - The formatted phone number
   * @param {Object} options - SMS options
   */
  const handleSMSFallback = useCallback(
    async (phoneNumber, options = {}) => {
      if (!phoneNumber) return false;

      try {
        const defaultOptions = {
          keepSessionOpen: false,
          locale,
        };

        await Login.sendSMS(phoneNumber, { ...defaultOptions, ...options });
        return true;
      } catch {
        return false;
      }
    },
    [locale],
  );

  return {
    isCheckingPasskey,
    passkeyAvailable,
    authenticateWithPasskey,
    checkPasskeyAvailability,
    handleSMSFallback,
  };
}
